"use client";

import Link from "next/link";
import { useEffect, useState, useSyncExternalStore } from "react";
import type { Image } from "@/lib/shopify/types";
import { cn } from "@/lib/format";
import { ProductImage } from "@/components/ui/ProductImage";

const ROTATE_MS = 1100;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const subscribeToMotion = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};

export type OccasionPreview = {
  title: string;
  path: string;
  description: string;
  products: { title: string; image: Image }[];
};

// The home page's occasion list. On wide screens a framed photo sits beside
// it: pointing at (or tabbing to) an occasion turns the photo through the
// products in that collection, in the collection's own order. Phones and
// tablets, which can't hover, show a small framed photo on each row instead.
// Reduced motion shows the first product only.
export function OccasionList({ occasions }: { occasions: OccasionPreview[] }) {
  const withPhotos = occasions.findIndex((occasion) => occasion.products.length);
  const [current, setCurrent] = useState(Math.max(withPhotos, 0));
  const [frame, setFrame] = useState(0);
  const [hovering, setHovering] = useState(false);
  const still = useSyncExternalStore(subscribeToMotion, () => window.matchMedia(REDUCED_MOTION).matches, () => true);

  const products = occasions[current]?.products ?? [];

  useEffect(() => {
    if (!hovering || still || products.length < 2) return;
    const timer = window.setInterval(() => setFrame((index) => (index + 1) % products.length), ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [hovering, still, products.length, current]);

  const show = (index: number) => {
    if (!occasions[index].products.length) return;
    if (index !== current) {
      setCurrent(index);
      setFrame(0);
    }
    setHovering(true);
  };

  return (
    <div className="shell grid gap-10 py-16 lg:grid-cols-[1fr_2fr] lg:gap-16 lg:py-24">
      <div>
        <h2 id="occasions" className="reveal font-display text-4xl leading-tight">
          What&apos;s the occasion?
        </h2>
        {withPhotos >= 0 ? (
          <figure className="reveal sticky top-32 mt-10 hidden theme-light rounded-[4px] bg-white p-3 shadow-[0_18px_40px_-22px_rgb(35_26_21/0.6)] [--step:1] lg:block">
            <div key={current} className="relative aspect-[4/5] animate-[rise_0.6s_var(--ease-soft)]">
              {products.map((product, index) => (
                <ProductImage
                  key={`${product.title}-${index}`}
                  image={product.image}
                  alt=""
                  sizes="(min-width: 1312px) 380px, 28vw"
                  fit="contain"
                  className={cn(
                    "absolute inset-0 rounded-[3px] transition-opacity duration-500 ease-in-out",
                    index === frame ? "opacity-100 [&_img]:animate-[settle_1.6s_var(--ease-soft)_both]" : "opacity-0",
                  )}
                />
              ))}
            </div>
            <figcaption className="px-1 pb-1 pt-3 text-sm">
              <span className="flex items-baseline justify-between gap-4">
                <span className="font-serif text-base italic">{occasions[current]?.title}</span>
                {products.length > 1 ? (
                  <span className="shrink-0 tabular-nums text-paper">
                    {frame + 1} of {products.length}
                  </span>
                ) : null}
              </span>
              <span className="mt-0.5 flex items-center justify-between gap-4">
                <span className="truncate text-paper">{products[frame]?.title}</span>
                {products.length > 1 ? (
                  <span className="flex shrink-0 gap-1" aria-hidden>
                    {products.map((product, index) => (
                      <span
                        key={`${product.title}-${index}`}
                        className={cn(
                          "h-[3px] rounded-full transition-all duration-500",
                          index === frame ? "w-4 bg-gold" : "w-[3px] bg-paper/30",
                        )}
                      />
                    ))}
                  </span>
                ) : null}
              </span>
            </figcaption>
          </figure>
        ) : null}
      </div>
      <ul className="self-start border-t border-paper/20" onMouseLeave={() => setHovering(false)}>
        {occasions.map((occasion, index) => (
          <li key={occasion.path} className="reveal" style={{ ["--step" as string]: Math.min(index, 5) }}>
            <Link
              href={occasion.path}
              className="group relative flex items-center justify-between gap-6 border-b border-paper/20 py-4 lg:items-baseline lg:py-5"
              onMouseEnter={() => show(index)}
              onFocus={() => show(index)}
              onBlur={() => setHovering(false)}
            >
              {/* A cocoa rule draws across under the hovered occasion. */}
              <span
                className={cn(
                  "absolute inset-x-0 -bottom-px h-px origin-left scale-x-0 bg-gold transition-transform duration-500 ease-[var(--ease-soft)] group-hover:scale-x-100 group-focus-visible:scale-x-100",
                )}
                aria-hidden
              />
              <span className="flex items-center gap-4">
                <span className="theme-light shrink-0 rounded-[4px] bg-white p-1.5 shadow-[0_10px_24px_-14px_rgb(35_26_21/0.55)] lg:hidden">
                  <ProductImage
                    image={occasion.products[0]?.image}
                    alt=""
                    sizes="80px"
                    fit="contain"
                    className="aspect-[4/5] w-16 rounded-[3px] sm:w-20"
                  />
                </span>
                <span className="font-display text-3xl transition-transform duration-500 ease-[var(--ease-soft)] group-hover:translate-x-2 group-focus-visible:translate-x-2 md:text-4xl">
                  {occasion.title}
                </span>
              </span>
              <span className="hidden max-w-xs text-right text-[0.9375rem] text-paper transition-colors group-hover:text-gold sm:block">
                {occasion.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
